import { memo, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { canDropOnRow, type PanelConfigPatch, type PanelDropPreview } from '../common/panel-grid';
import { type PageCmsPanelRow, type PanelSize } from '../common/panel-types';
import { PanelRow } from '../CustomPage.styled';
import { PanelDropPreviewRow } from './PanelDropPreviewRow';
import { PanelItem } from './PanelItem';

const messages = defineMessages({
  row: {
    id: 'editCustomPageRow',
    defaultMessage: 'Row {row}'
  }
});

interface PanelGridRowProps {
  readonly row: PageCmsPanelRow;
  readonly rowIndex: number;
  readonly onSizeChange: (rowIndex: number, panelId: string, size: PanelSize) => void;
  readonly onDeletePanel: (rowIndex: number, panelId: string) => void;
  readonly onMovePanel?: (panelId: string, trigger: HTMLElement) => void;
  readonly onConfigChange: (rowIndex: number, panelId: string, patch: PanelConfigPatch) => void;
  readonly showPanelErrors: boolean;
  readonly dropPreview?: PanelDropPreview | null;
}

const PanelGridRowComponent = ({
  row,
  rowIndex,
  onSizeChange,
  onDeletePanel,
  onMovePanel,
  onConfigChange,
  showPanelErrors,
  dropPreview = null
}: PanelGridRowProps) => {
  const intl = useIntl();
  const rowRef = useRef<HTMLDivElement>(null);
  const panelCount = row.length;

  // Fallback target for the row's gaps and padding; the panels' own targets sit inside it and win.
  useEffect(() => {
    const element = rowRef.current;

    if (!element) {
      return undefined;
    }

    return dropTargetForElements({
      element,
      getData: () => ({ type: 'row', rowIndex }),
      canDrop: ({ source }) => canDropOnRow(source.data, rowIndex, panelCount)
    });
  }, [rowIndex, panelCount]);

  return (
    <PanelRow
      ref={rowRef}
      role="group"
      aria-label={intl.formatMessage(messages.row, { row: rowIndex + 1 })}
      data-testid={`panel-grid-row-${rowIndex}`}
    >
      {row.map((panel, panelIndex) => (
        <PanelItem
          key={panel.id}
          panel={panel}
          panelCount={panelCount}
          rowIndex={rowIndex}
          panelIndex={panelIndex}
          onSizeChange={onSizeChange}
          onDeletePanel={onDeletePanel}
          onMovePanel={onMovePanel}
          onConfigChange={onConfigChange}
          showPanelErrors={showPanelErrors}
        />
      ))}
      {dropPreview && (
        <PanelDropPreviewRow
          row={dropPreview.row}
          incomingPanelId={dropPreview.incomingPanelId}
          showPanelErrors={showPanelErrors}
        />
      )}
    </PanelRow>
  );
};

export const PanelGridRow = memo(PanelGridRowComponent);
